import { Fragment, useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

// Tiers are complete plans. Premier/Premier Plus/Prime ride on Outpatient Plan 1 and Dental
// Care Plan 1; Prestige/Prestige Plus ride on Plan 2 instead — same field, different tier
// "family" — matching isPlan1Enable/isPlan2Enable in ProMedico_Rates.js. Maternity applies
// to every tier.
const TIERS = [
    { key: 'premier', label: 'Premier', family: 'basic' },
    { key: 'premier_plus', label: 'Premier Plus', family: 'basic' },
    { key: 'prime', label: 'Prime', family: 'basic' },
    { key: 'prestige', label: 'Prestige', family: 'prestige' },
    { key: 'prestige_plus', label: 'Prestige Plus', family: 'prestige' },
];

// Riders are selected by key; the rate field depends on the tier's family
const RIDERS = [
    {
        key: 'outpatient',
        heading: 'Outpatient Services',
        tableHeading: 'Outpatient Services - Ride to Basic Hospital & Surgical Coverage',
        fields: { basic: 'outpatient_plan_1', prestige: 'outpatient_plan_2' },
        labels: { basic: 'Plan 1', prestige: 'Plan 2' },
    },
    {
        key: 'dental_care',
        heading: 'Dental Care (Optional)',
        tableHeading: 'Dental Care (Optional)',
        fields: { basic: 'dental_care_plan_1', prestige: 'dental_care_plan_2' },
        labels: { basic: 'Plan 1', prestige: 'Plan 2' },
    },
    {
        key: 'maternity',
        heading: 'Maternity Care (Optional)',
        tableHeading: 'Maternity Care (Optional)',
        fields: { basic: 'maternity', prestige: 'maternity' },
        labels: { basic: 'Maternity', prestige: 'Maternity' },
    },
];

// Every rate field the API returns — all are kept so switching tier or riders needs no refetch
const RATE_FIELDS = [
    ...TIERS.map(t => t.key),
    'outpatient_plan_1', 'outpatient_plan_2', 'dental_care_plan_1', 'dental_care_plan_2', 'maternity',
];

// ProMedico is SGD-only — ProMedico_Rates.js hardcodes this locally rather than reading
// appConfig.content.currencies, so this dialog mirrors that instead of offering other currencies.
const CURRENCIES = ['SGD'];
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#ffcf02';
const BRAND_TEXT_COLOR = '#000';
const NAVY = '#1a1446';
const SECTION_COLOR = '#99e5ea';

function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return 'S$' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as ProMedico_Rates: discount/loading scale the tier and every rider
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function ProMedico_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.pro_medico_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Singapore Only') ? 'Singapore Only' : (areaOptions[0] || '');
    const getDeductibles = (curr) => appConfig?.content?.deductibles?.pro_medico?.[curr] || [];

    const familyDiscounts = appConfig?.content?.specifics?.pro_medico?.family_discounts || {};
    const familyDiscountOptions = Object.keys(familyDiscounts);

    const [tier, setTier] = useState('premier');
    const [selectedRiders, setSelectedRiders] = useState(new Set());
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('SGD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [familyDiscount, setFamilyDiscount] = useState(familyDiscountOptions[0] || '');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedTierObj = TIERS.find(t => t.key === tier) || TIERS[0];
    const family = selectedTierObj.family;
    const activeRiders = RIDERS.filter(r => selectedRiders.has(r.key));

    function getRiderLabel(rider, riderFamily = family) {
        return rider.key === 'maternity' ? 'Maternity' : `${rider.heading.replace(' (Optional)', '')} (${rider.labels[riderFamily]})`;
    }

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = getDeductibles('SGD')[0] || '';
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            const savedTier = editingPlan.plan || (editingPlan.plan_id || '').replace('pro_medico_', '') || 'premier';
            const savedFamily = (TIERS.find(t => t.key === savedTier) || TIERS[0]).family;
            setTier(savedTier);
            setSelectedRiders(new Set(editingPlan.payloads?.riders || []));
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'SGD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setFamilyDiscount(editingPlan.payloads?.family_discount || familyDiscountOptions[0] || '');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored per-category premiums — only the saved tier and
            // its family's rider fields are recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    const obj = { [savedTier]: unscale(premium?.tier_premium ?? null) };
                    RIDERS.forEach(r => { obj[r.fields[savedFamily]] = unscale(premium?.[`${r.key}_premium`] ?? null); });
                    return obj;
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open) {
            setTier('premier');
            setSelectedRiders(new Set());
            setAreaOfCoverage(defaultArea);
            setCurrency('SGD');
            setPaymentFrequency('Annually');
            setFamilyDiscount(familyDiscountOptions[0] || '');
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const first = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${first?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { pro_medico: quoteDeductibles[quote.name] || '' },
                            insurers: ['pro_medico'],
                            specifics: { pro_medico: { family_discount: familyDiscount } },
                            specific_area_of_coverage: { pro_medico: areaOfCoverage },
                            rates_version_per_insurer: { pro_medico: selectedRatesVersion?.pro_medico || '' },
                            specific_currency: { pro_medico: currency },
                            specific_payment_frequency: { pro_medico: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.pro_medico }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Liberty proMedico rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Rate for one field with discount/loading applied; ratesData keeps the raw API rates
    function getFieldRate(clientIndex, field, quoteUid) {
        const val = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[field];
        if (val === undefined || val === null) return null;
        return typeof val === 'number' ? val * rateFactor : val;
    }

    function getRiderRate(clientIndex, rider, quoteUid) {
        return getFieldRate(clientIndex, rider.fields[family], quoteUid);
    }

    // Cumulative rate for the tier plus every selected rider
    function getTotal(clientIndex, quoteUid) {
        let total = 0;
        let hasAny = false;
        [getFieldRate(clientIndex, tier, quoteUid), ...activeRiders.map(r => getRiderRate(clientIndex, r, quoteUid))].forEach(rate => {
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        });
        return hasAny ? total : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Tier and rider rates for every tier come back in one fetch, so toggling a rider just
    // shows a different set of rows — no refresh needed.
    function toggleRider(key) {
        setSelectedRiders(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function buildPlanLabel() {
        let label = selectedTierObj.label;
        if (activeRiders.length > 0) {
            label += ' + ' + activeRiders.map(r => getRiderLabel(r)).join(' + ');
        }
        return label;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getTotal(clientIndex, quote.uid);

                // Stored with discount/loading applied, matching premium_rate
                const premium = {
                    insurer: 'pro_medico',
                    plan: tier,
                    plan_label: planLabel,
                    included_benefits: '',
                    deductible: quoteDeductibles[quote.name] || '',
                    area_of_coverage: areaOfCoverage,
                    currency,
                    payment_frequency: paymentFrequency,
                    family_discount: familyDiscount,
                    discount,
                    loading: loadingPercent,
                    premium_rate: rate ?? null,
                    tier_premium: getFieldRate(clientIndex, tier, quote.uid),
                };

                RIDERS.forEach(r => {
                    premium[`${r.key}_premium`] = selectedRiders.has(r.key) ? getRiderRate(clientIndex, r, quote.uid) : null;
                });

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: `pro_medico_${tier}`,
                    planUid,
                    premium,
                });
            });
        });

        const plan = {
            plan_id: `pro_medico_${tier}`,
            uid: planUid,
            plan: tier,
            insurer: 'pro_medico',
            label: `Liberty proMedico ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                family_discount: familyDiscount,
                riders: [...selectedRiders],
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const deductibleOptions = getDeductibles(currency);
    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };
    const checkboxLabelSx = { mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } };

    function renderSectionRow(label) {
        return (
            <TableRow>
                <TableCell colSpan={clients.length + 2} sx={{ fontWeight: 600, background: SECTION_COLOR }}>{label}</TableCell>
            </TableRow>
        );
    }

    function renderRateRow(key, label, getter, rowIdx) {
        return (
            <TableRow key={key} sx={{ backgroundColor: rowIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                <TableCell sx={{ color: 'text.secondary' }}>{label}</TableCell>
                {clients.map((client, idx) => (
                    <TableCell key={client.uid || idx}>{formatRate(getter(idx))}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: 500 }}>{formatRate(sumClients(getter))}</TableCell>
            </TableRow>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: NAVY,
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    width: 100,
                    height: 60,
                }}>Liberty proMedico</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/images/Liberty Blue Logo.png`}
                        alt="Liberty"
                        style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }}
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                    />
                    <IconButton size="small" onClick={onClose} sx={{ color: NAVY, ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                <Box>
                    {/* Plan Type */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                                value={tier}
                                disabled={loading || !!editingPlan}
                                // Rates for every tier come back in one fetch, so switching tiers just shows different rows
                                onChange={(e) => setTier(e.target.value)}
                                sx={selectSx}
                            >
                                {TIERS.map(t => (
                                    <MenuItem key={t.key} value={t.key} sx={menuItemSx}>{t.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Outpatient Services — Plan 1 for Premier/Premier Plus/Prime, Plan 2 for Prestige/Prestige Plus */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 15 }}>{RIDERS[0].heading}</Typography>
                        <FormControlLabel
                            sx={checkboxLabelSx}
                            control={<Checkbox size="small" checked={selectedRiders.has('outpatient')} onChange={() => toggleRider('outpatient')} disabled={loading || !!editingPlan} />}
                            label={RIDERS[0].labels[family]}
                        />
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    {RIDERS.slice(1).map((rider, idx) => (
                        <Box key={rider.key} sx={{ display: 'flex', flexDirection: 'column', mt: idx > 0 ? 1 : 0 }}>
                            <Typography fontWeight={600} sx={{ fontSize: 15 }}>{rider.heading}</Typography>
                            <FormControlLabel
                                sx={checkboxLabelSx}
                                control={<Checkbox size="small" checked={selectedRiders.has(rider.key)} onChange={() => toggleRider(rider.key)} disabled={loading || !!editingPlan} />}
                                label={rider.labels[family]}
                            />
                        </Box>
                    ))}
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        {familyDiscountOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Family Discount</Typography>
                                <CustomSelect2
                                    dense
                                    sx={{ minWidth: 170 }}
                                    value={familyDiscount}
                                    onChange={(e) => { setFamilyDiscount(e.target.value); invalidateRates(); }}
                                    items={familyDiscountOptions}
                                    disabled={loading}
                                />
                            </>
                        )}

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {areaOptions.map(area => <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={currency} disabled={loading} onChange={(e) => { setCurrency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ minWidth: 200, fontSize: 13, fontWeight: 600 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: BRAND_TEXT_COLOR, fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: BRAND_TEXT_COLOR, fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: BRAND_TEXT_COLOR, fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Same sections as ProMedico_Rates */}
                                            {renderSectionRow('Basic Hospital & Surgical Coverage')}
                                            {renderRateRow(tier, selectedTierObj.label, i => getFieldRate(i, tier, quote.uid), 0)}
                                            {activeRiders.map(rider => (
                                                <Fragment key={rider.key}>
                                                    {renderSectionRow(rider.tableHeading)}
                                                    {renderRateRow(rider.key, rider.labels[family], i => getRiderRate(i, rider, quote.uid), 0)}
                                                </Fragment>
                                            ))}
                                            {/* Total row — same as ProMedico_Rates' "Total Premium (<tier>)" */}
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, background: NAVY, color: '#fff' }}>Total Premium ({selectedTierObj.label})</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ fontWeight: 600, background: NAVY, color: '#fff' }}>
                                                        {formatRate(getTotal(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 600, background: NAVY, color: '#fff' }}>
                                                    {formatRate(sumClients(i => getTotal(i, quote.uid)))}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="pro_medico" />

                <RatesVersionSelect
                    insurer="pro_medico"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}

